import {
  APIuuid,
  IAPIArrayData,
  IAPIData,
  IAPIDataObject,
  IAPIRelationship
} from '@shared/interfaces/api.interface';
import { IHiringFirm } from '@shared/interfaces/hiring-firm.interface';
import { IReferenceRelationship } from '@shared/interfaces/reference-relationship.interface';
import { IRelationshipLanguage } from '@shared/interfaces/relationship-language.interface';

export class ReferenceRelationship implements IReferenceRelationship {
  public static readonly TYPE: string = String('ReferenceRelationship');

  public id: APIuuid;

  public hiring_firm: Partial<IHiringFirm>;

  public value: string;
  public is_active: boolean;

  public relationship_languages?: IRelationshipLanguage[];

  // Legacy fields for backward compatibility
  public label?: string;
  public language?: string;

  public question_set?: IAPIRelationship;
  public prevent_personal_emails?: boolean;

  constructor(partial: Partial<IReferenceRelationship>) {
    Object.assign(this, partial);
  }

  /**
   * Get labels as an object with language codes as keys
   * Converts relationship_languages array to { en: 'label', fr: 'label', es: 'label' } format
   * for backward compatibility with templates
   */
  public get labels(): { [key: string]: string } {
    if (!this.relationship_languages || this.relationship_languages.length === 0) {
      return {};
    }

    return this.relationship_languages.reduce((acc, lang) => {
      acc[lang.code] = lang.label;
      return acc;
    }, {} as { [key: string]: string });
  }

  /**
   * Get label for a specific language
   */
  public getLabelFor(languageCode: string): string {
    if (this.relationship_languages) {
      const lang = this.relationship_languages.find((l) => l.code === languageCode);
      if (lang) {
        return lang.label;
      }
    }
    // Fallback to legacy label if available
    return this.label || '';
  }

  public get apiData(): IAPIData {
    const relationships: any = {
      hiring_firm: {
        id: this.hiring_firm?.id
      } as IAPIRelationship
    };

    if (this.question_set !== undefined) {
      relationships.question_set = {
        id: this.question_set?.id || null
      } as IAPIRelationship;
    }

    const attributes: any = {
      value: this.value,
      prevent_personal_emails: this.prevent_personal_emails
    };

    // Add relationship_languages_attributes for nested attributes in Rails
    if (this.relationship_languages && this.relationship_languages.length > 0) {
      attributes.relationship_languages_attributes = this.relationship_languages.map(
        (lang: IRelationshipLanguage) => ({
          id: lang.id,
          code: lang.code,
          label: lang.label
        })
      );
    }

    // Legacy support: if label and language are provided, use them
    if (this.label && this.language) {
      attributes.label = this.label;
      attributes.language = this.language;
    }

    return {
      data: {
        id: this.id,
        type: ReferenceRelationship.TYPE,
        attributes,
        relationships
      }
    } as IAPIData;
  }

  public static fromAPI(answerSetInfo: IAPIData): IReferenceRelationship {
    const data = answerSetInfo.data;
    if (!(data && data.type === ReferenceRelationship.TYPE)) {
      throw new Error(
        `There was a problem parsing ${ReferenceRelationship.TYPE} API Data`
      );
    }

    const rr = new ReferenceRelationship({
      id: data.id,
      ...data.attributes,
      question_set: data.relationships?.question_set,
      hiring_firm: data.relationships?.hiring_firm
    });

    return rr;
  }

  static fromAPIArray(res: IAPIArrayData): IReferenceRelationship[] {
    const dataArray = res.data;

    return dataArray.map((data: IAPIDataObject) => {
      if (!(data && data.type === ReferenceRelationship.TYPE)) {
        throw new Error(
          `There was a problem parsing ${ReferenceRelationship.TYPE} API data`
        );
      }

      const partial = {
        id: data.id,
        ...data.attributes,
        question_set: data.relationships?.question_set,
        hiring_firm: data.relationships?.hiring_firm
      };

      const rr = new ReferenceRelationship(partial);
      return rr;
    });
  }
}
